import { useState, useCallback } from "react";
import { SimpleGrid, Button } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Loading from "../Component/Loading/loading";
import apiService from "../Method/Account_Service";
import { ThemeProvider } from "@mui/material/styles";
import MUIDataTable from "mui-datatables";
import theme from "../assets/Table/TableTheme";
import options from "../assets/Table/TableOption_NoPrint";
import { EditIcon } from "@chakra-ui/icons";
import { IconButton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faUnlockAlt } from "@fortawesome/free-solid-svg-icons";
import InputText from "../Component/Input/Input";
import Swal from "sweetalert2";

const App = () => {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [selectedSubAccounts, setSelectedSubAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ PINCODE: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setSelectedSubAccounts([]);
    try {
      const SearchData = {
        "PIN_CODE": parseInt(form.PINCODE),
      };
      const { success, data, error } = await apiService.getByPinCode(SearchData);
      if (success) {
        setData(Array.isArray(data) ? data : [data]);
      } else {
        setError(error.response?.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.PINCODE) {
      setError(`يجب إدخال ${t("PIN_CODE")}`);
      return;
    }
    setError(null);
    fetchData();
  };

  const handleViewSubAccounts = (tableMeta) => {
    const row = data[tableMeta.rowIndex];
    setSelectedSubAccounts(row.SUB_ACCOUNT || []);
    setAccount(row);
  };

  const toggleBlock = async (data) => {
    const row = selectedSubAccounts[data.rowIndex];
    const isBlocked = row.IS_BLOCK;

    await Swal.fire({
      icon: isBlocked ? "success" : "warning",
      title: isBlocked
        ? `${t("Confirm_msg_UnBlock")} ${row.USER_NAME}`
        : `${t("Confirm_msg_Block")} ${row.USER_NAME}`,
      focusConfirm: false,
      showCancelButton: true,
      customClass: {
        container: "alertContainer",
        popup: "alertPopUp",
        title: "title",
        confirmButton: "confirm",
        cancelButton: "cancel",
      },
      preConfirm: async () => {
        try {
          const sendData = {
            "PIN_CODE": account.PIN_CODE,
            "USER_NAME": row.USER_NAME,
            "PASSWORD": row.PASSWORD,
            "DEVICE_ID": row.DEVICE_ID,
            "IS_BLOCK": !isBlocked,
          };

          const { success, error } = await apiService.post(sendData, "UPDATE_ACCOUNT_BLOCK_STATUS");

          if (success) {
            fetchData();
            Swal.fire({
              icon: "success",
              title: isBlocked ? t("UnBlocked_Successfully") : t("Blocked_Successfully"),
            });
          } else {
            setError(error?.message);
          }
        } catch (err) {
          setError(err.message);
        }
      },
    });
  };

  const tableColumns = [
    { name: "_id", label: "ID", options: { filter: false, sort: false, display: false, viewColumns: false } },
    { name: "PIN_CODE", label: t("PIN_CODE"), options: { filter: false, sort: false } },
    { name: "INVOICE_NO", label: t("INVOICE_NO"), options: { filter: false, sort: false } },
    { name: "EXPIRE_DATE", label: t("EXPIRE_DATE"), options: { filter: false, sort: false } },
    { name: "MAX_COUNT", label: t("MAX_COUNT"), options: { filter: false, sort: false } },
    {
      name: "ACTIVE",
      label: t("ACTIVE"),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: value === true ? "green" : "red",
              }}
            ></div>
          </div>
        ),
      },
    },
    {
      name: "",
      label: t("View_Sub_Accounts"),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => (
          <IconButton
            className="TableIcon"
            onClick={() => handleViewSubAccounts(tableMeta)}
          >
            <EditIcon />
          </IconButton>
        ),
      },
    },
  ];

  const subAccountColumns = [
    { name: "USER_NAME", label: t("USER_NAME"), options: { filter: false, sort: false } },
    { name: "DEVICE_ID", label: t("DEVICE_ID"), options: { filter: false, sort: false } },
    {
      name: "IS_BLOCK",
      label: t("IS_BLOCK"),
      options: {
        filter: false, sort: false,
        customBodyRender: (value) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: value === true ? "green" : "red",
              }}
            ></div>
          </div>
        ),
      },
    },
    {
      name: "",
      label: t(""),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const row = selectedSubAccounts[tableMeta.rowIndex];
          return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <FontAwesomeIcon
                icon={row.IS_BLOCK === true ? faUnlockAlt : faLock}
                style={{ color: row.IS_BLOCK === true ? "green" : "red", cursor: "pointer", fontSize: 18 }}
                title={row.IS_BLOCK === true ? "فك الحظر" : "حظر"}
                onClick={() => toggleBlock(tableMeta)}
              />
            </div>
          );
        },
      },
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <form onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} gap={5} p={5}>
          <InputText
            type="number"
            value={form.PINCODE}
            labels={t("PIN_CODE")}
            onChange={(v) => setForm((f) => ({ ...f, PINCODE: v }))}
          />
         
          <Button type="submit" className="login-btn">
            {t("save")}
          </Button>
        </SimpleGrid>
      </form>

      {error && <p className="error-message">{error}</p>}
      {isLoading && <Loading />}
      {!isLoading && (
        <>
          <MUIDataTable
            title=""
            data={Array.isArray(data) ? data : []}
            columns={tableColumns}
            options={options}
          />

          {selectedSubAccounts.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <MUIDataTable
                title={t("Sub_Accounts")}
                data={selectedSubAccounts}
                columns={subAccountColumns}
                options={options}
              />
            </div>
          )}
        </>
      )}
    </ThemeProvider>
  );
};

export default App;
